import {ChangeDetectionStrategy, Component, computed, inject, input, output} from '@angular/core';
import {TranslocoService} from '@jsverse/transloco';
import {NgClass} from '@angular/common';
import {Tooltip} from 'primeng/tooltip';
import {AttributeConsistencyStatus, AttributeConsistencyView} from '@frontend/shared-api';
import {
  ATTRIBUTE_CONSISTENCY_STATUS_COLORS,
  ATTRIBUTE_CONSISTENCY_STATUS_LABEL_KEYS,
} from '../../constants/attribute-consistency.constants';

@Component({
  selector: 'app-attribute-consistency-status',
  imports: [NgClass, Tooltip],
  templateUrl: './attribute-consistency-status.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttributeConsistencyStatusComponent {
  private readonly transloco = inject(TranslocoService);

  readonly consistency = input<AttributeConsistencyView>();
  readonly testId = input('');

  readonly detailsRequested = output<void>();

  protected readonly status = computed<AttributeConsistencyStatus | undefined>(() => {
    const consistency = this.consistency();

    return consistency === undefined || consistency.ratings.length === 0
      ? undefined
      : consistency.status;
  });

  protected readonly icon = computed<string>(() => {
    switch (this.status()) {
      case AttributeConsistencyStatus.MANUALLY_ACCEPTED:
        return 'pi-check-circle';
      case AttributeConsistencyStatus.CLARIFICATION_REQUIRED:
        return 'pi-times-circle';
      case AttributeConsistencyStatus.REVIEW_REQUIRED:
        return 'pi-exclamation-triangle';
      default:
        return 'pi-check';
    }
  });

  protected readonly color = computed<string>(() => {
    const status = this.status();

    return status === undefined ? '' : ATTRIBUTE_CONSISTENCY_STATUS_COLORS[status];
  });

  protected readonly label = computed<string>(() => {
    const status = this.status();

    if (status === undefined) {
      return '';
    }

    const statusLabel = this.transloco.translate(ATTRIBUTE_CONSISTENCY_STATUS_LABEL_KEYS[status]);

    return `${statusLabel} · ${Math.round(this.consistency()?.homogeneity ?? 0)} %`;
  });

  onDetailsRequested(): void {
    this.detailsRequested.emit();
  }
}
